// Opens a print-ready A4-landscape Gantt for a project in a new window and
// triggers the browser print dialog. Overflowing activities paginate to the
// next A4 page with the timeline header + legend repeated.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ROWS_PER_PAGE = 12;
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const dOf = (s) => new Date(String(s).slice(0, 10) + 'T00:00:00Z');
const daysBetween = (a, b) => Math.round((dOf(b) - dOf(a)) / 864e5);
const todayFmt = () => new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export function printGantt(project, meta = {}) {
  if (!project) return;
  // Build rows (milestones + tasks) like the on-screen Gantt.
  const rows = [];
  const allDates = [];
  for (const m of (project.milestones || [])) {
    const tasks = m.tasks || [];
    const done = tasks.filter(t => t.done || t.pct_complete >= 100).length;
    const mPct = tasks.length ? Math.round((done / tasks.length) * 100) : null;
    rows.push({ ms: 1, label: m.title, ps: m.start_date, pe: m.end_date, as: m.actual_start, ae: m.actual_end, pct: mPct });
    for (const t of tasks) {
      const pct = t.pct_complete != null ? t.pct_complete : (t.done ? 100 : 0);
      rows.push({ ms: 0, label: t.title, ps: t.planned_start, pe: t.planned_end, as: t.actual_start, ae: t.actual_end, pct });
    }
  }
  rows.forEach(r => [r.ps, r.pe, r.as, r.ae].forEach(x => x && allDates.push(String(x).slice(0, 10))));
  if (!allDates.length) { alert('No planned or actual dates to print yet. Add task dates first.'); return; }
  allDates.sort();
  const today = new Date().toISOString().slice(0, 10);
  const min = allDates[0];
  const max = allDates[allDates.length - 1];
  const span = Math.max(1, daysBetween(min, max) + 1);
  const todayLeft = Math.max(0, Math.min(100, daysBetween(min, today) / span * 100));
  // A completed project is a historical record — frame it to its own planned/actual
  // dates and don't anchor it to today (no "Today" marker/legend).
  const isCompleted = project.status === 'completed' || !!project.actual_date;
  const showToday = !isCompleted && today >= min && today <= max;

  const todayDiv = showToday ? `<div class="today" style="left:${todayLeft}%"></div>` : '';
  const weeks = Math.ceil(span / 7);
  const labelEvery = weeks <= 22 ? 1 : weeks <= 44 ? 2 : 4;
  // Gridlines: a thin line each week + a darker line at each month start.
  const gridlines = () => {
    let out = '';
    for (let i = 1; i < weeks; i++) out += `<div class="gl" style="left:${i * 7 / span * 100}%"></div>`;
    const c = dOf(min); c.setUTCDate(1);
    while (c <= dOf(max)) { const off = daysBetween(min, c.toISOString().slice(0, 10)); if (off > 0) out += `<div class="glm" style="left:${off / span * 100}%"></div>`; c.setUTCMonth(c.getUTCMonth() + 1); }
    return out;
  };
  // Header: month names (top) + week numbers Wk1, Wk2… (bottom).
  const monthLabels = () => {
    let out = ''; const c = dOf(min); c.setUTCDate(1);
    while (c <= dOf(max)) { const off = daysBetween(min, c.toISOString().slice(0, 10)); if (off >= 0) out += `<div class="mlab" style="left:${off / span * 100}%">${MONTHS[c.getUTCMonth()]} ${String(c.getUTCFullYear()).slice(2)}</div>`; c.setUTCMonth(c.getUTCMonth() + 1); }
    return out;
  };
  const weekLabels = () => {
    let out = '';
    for (let i = 0; i < weeks; i++) { if (i % labelEvery === 0) out += `<div class="wlab" style="left:${i * 7 / span * 100}%">Wk${i + 1}</div>`; }
    return out;
  };
  const barDiv = (ps, pe, top, color, h) => {
    if (!ps || !pe) return '';
    const l = daysBetween(min, ps) / span * 100, w = (daysBetween(ps, pe) + 1) / span * 100;
    return `<div class="bar" style="left:${l}%;width:${w}%;top:${top}px;height:${h}px;background:${color}"></div>`;
  };
  const rowHtml = (r) => {
    if (r.ms) return `<tr><td class="lab ms">◆ ${esc(r.label)}</td><td class="tlc"><div class="cell">${gridlines()}${todayDiv}</div></td></tr>`;
    const overdue = r.pe && r.pe < today && !r.ae && r.pct < 100;
    const actualColor = (r.ae && r.pe && r.ae > r.pe) ? 'var(--late)' : 'var(--ontime)';
    let bars = gridlines() + todayDiv;
    if (r.ps && r.pe) {
      const l = daysBetween(min, r.ps) / span * 100, w = (daysBetween(r.ps, r.pe) + 1) / span * 100;
      const bg = overdue ? 'repeating-linear-gradient(45deg,#f59e0b,#f59e0b 2px,#fde68a 2px,#fde68a 4px)' : 'var(--planned)';
      bars += `<div class="bar" style="left:${l}%;width:${w}%;top:4px;height:7px;background:${bg}"></div>`;
      if (r.pct > 0) bars += `<div class="bar" style="left:${l}%;width:${w * Math.min(r.pct, 100) / 100}%;top:4px;height:7px;background:var(--prog)"></div>`;
    }
    bars += barDiv(r.as, r.ae, 13, actualColor, 7);
    return `<tr><td class="lab task">${esc(r.label)}${overdue ? ' ⚠' : ''}</td><td class="tlc"><div class="cell">${bars}</div></td></tr>`;
  };
  const headerRow = `<tr class="ghead"><td class="lab">Activity</td><td class="tlc"><div class="cell hdr">${monthLabels()}${weekLabels()}</div></td></tr>`;

  const pages = [];
  for (let i = 0; i < rows.length; i += ROWS_PER_PAGE) pages.push(rows.slice(i, i + ROWS_PER_PAGE));
  const title = meta.title || project.title || 'Project';
  const number = meta.number || project.project_number || '';
  const client = meta.client || project.customer_name || project.company_name || '';
  const manager = meta.manager || project.manager_name || '';
  const asOf = todayFmt();
  const metaLine = [number, client, manager && `Manager: ${manager}`, `${min} → ${max}`].filter(Boolean).map(esc).join(' · ') + ` &middot; Printed: ${esc(asOf)}`;

  const sheet = (pageRows, n, tot) => `
    <div class="sheet"><div class="pg">Page ${n} of ${tot}</div>
      <div class="head"><div><div class="co">KGreen Consulting &amp; Technologies Pvt Ltd</div>
        <h2>${esc(title)}</h2><div class="meta">${metaLine}</div>
        <div class="legend">
          <span class="lg"><span class="sw" style="background:var(--planned)"></span>Planned</span>
          <span class="lg"><span class="sw" style="background:var(--prog)"></span>Progress</span>
          <span class="lg"><span class="sw" style="background:var(--ontime)"></span>Actual (on time)</span>
          <span class="lg"><span class="sw" style="background:var(--late)"></span>Actual (late)</span>
          <span class="lg"><span class="sw" style="background:repeating-linear-gradient(45deg,#f59e0b,#f59e0b 2px,#fde68a 2px,#fde68a 4px)"></span>Slipping</span>
          ${showToday ? '<span class="lg"><span style="color:var(--late);font-weight:800">│</span> Today</span>' : ''}
        </div>
      </div>
      <table class="gantt"><colgroup><col class="cL"/><col/></colgroup>
        <thead>${headerRow}</thead>
        <tbody>${pageRows.map(rowHtml).join('')}</tbody>
      </table>
      <div class="foot">KGreen Consulting &amp; Technologies Pvt Ltd · Project Gantt · Generated ${esc(asOf)}</div>
    </div>`;

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Gantt — ${esc(title)}</title>
<style>
:root{--navy:#0F1E3D;--kg:#0B6623;--line:#e2e8f0;--t2:#64748b;--planned:#cfe0fb;--prog:#2563eb;--ontime:#16a34a;--late:#dc2626;}
*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact;} body{margin:0;background:#64748b;font-family:'Inter',system-ui,Arial,sans-serif;color:#0f172a;}
.sheet{width:297mm;min-height:210mm;background:#fff;margin:0 auto 14px;padding:12mm 12mm 10mm;position:relative;box-shadow:0 6px 24px rgba(0,0,0,.25);}
.pg{position:absolute;top:6mm;right:12mm;font-size:10px;color:var(--t2);font-weight:600;}
.head{display:flex;justify-content:space-between;border-bottom:2px solid var(--navy);padding-bottom:8px;margin-bottom:10px;}
.co{font-size:11px;font-weight:800;letter-spacing:2px;color:var(--kg);text-transform:uppercase;}
.head h2{font-size:17px;margin:3px 0 2px;} .meta{font-size:11px;color:var(--t2);}
.legend{display:flex;gap:12px;font-size:10px;align-items:center;flex-wrap:wrap;margin-top:4px;}
.lg{display:flex;align-items:center;gap:4px;} .sw{width:14px;height:8px;border-radius:2px;display:inline-block;}
.gantt{width:100%;border-collapse:collapse;table-layout:fixed;border:1px solid var(--line);}
.gantt col.cL{width:58mm;}
.gantt td{border-bottom:1px solid #eef1f6;padding:0;vertical-align:middle;}
.gantt tr:last-child td{border-bottom:none;}
.lab{padding:4px 8px;font-size:11px;border-right:1px solid var(--line);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.lab.ms{font-weight:800;background:#f9fafb;} .lab.task{padding-left:16px;color:#334155;}
.ghead td{background:#f6f8fb;}
.cell{position:relative;height:22px;width:100%;overflow:hidden;}
.cell.hdr{height:30px;}
.gl{position:absolute;top:0;bottom:0;width:1px;background:#f1f5f9;}
.glm{position:absolute;top:0;bottom:0;width:1px;background:#cbd5e1;}
.mlab{position:absolute;top:0;height:15px;display:flex;align-items:center;font-size:9px;color:var(--t2);font-weight:700;padding-left:3px;border-left:1px solid var(--line);white-space:nowrap;}
.wlab{position:absolute;top:15px;height:15px;display:flex;align-items:center;font-size:7.5px;color:#94a3b8;font-weight:700;padding-left:2px;border-left:1px solid #eef1f6;white-space:nowrap;}
.bar{position:absolute;border-radius:3px;} .today{position:absolute;top:0;bottom:0;width:1.5px;background:rgba(220,38,38,.6);z-index:2;}
.foot{font-size:9px;color:var(--t2);text-align:center;margin-top:8px;border-top:1px solid var(--line);padding-top:6px;}
@media print{body{background:#fff;}.sheet{box-shadow:none;margin:0;page-break-after:always;}@page{size:A4 landscape;margin:0;}}
</style></head><body>${pages.map((p, i) => sheet(p, i + 1, pages.length)).join('')}
<script>window.onload=function(){setTimeout(function(){window.print();},300);};<\/script>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('Allow pop-ups to print the Gantt.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
}

// Print a project OVERVIEW summary (facts + milestone/task progress) on A4 portrait.
export function printProjectOverview(project, meta = {}) {
  if (!project) return;
  const fmt = (s) => { if (!s) return '—'; const d = new Date(String(s).slice(0, 10) + 'T00:00:00Z'); return isNaN(d) ? s : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const ms = project.milestones || [];
  const totalTasks = ms.reduce((a, m) => a + (m.tasks || []).length, 0);
  const doneTasks = ms.reduce((a, m) => a + (m.tasks || []).filter(t => t.done || t.pct_complete >= 100).length, 0);
  const pct = totalTasks ? Math.round(doneTasks / totalTasks * 100) : 0;
  const facts = [
    ['Project #', project.project_number], ['Status', project.status],
    ['Customer', project.customer_name || project.company_name], ['Product', project.product_name || '—'],
    ['Manager', project.manager_name || '—'], ['Type', project.type || '—'],
    ['Start', fmt(project.start_date)], ['Target', fmt(project.target_date)],
    ['Actual', fmt(project.actual_date)], ['Outcome', project.outcome || '—'],
  ];
  const msRows = ms.map(m => {
    const t = m.tasks || [];
    const dn = t.filter(x => x.done || x.pct_complete >= 100).length;
    return `<tr><td><b>${esc(m.title)}</b></td><td>${esc(m.status || '')}</td><td>${fmt(m.start_date)}</td><td>${fmt(m.end_date || m.due_date)}</td><td style="text-align:center">${dn}/${t.length}</td></tr>`;
  }).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(project.title || 'Project')} — Overview</title>
<style>
:root{--navy:#0F1E3D;--kg:#0B6623;--line:#e2e8f0;--t2:#64748b;}
*{box-sizing:border-box} body{margin:0;background:#64748b;font-family:'Inter',system-ui,Arial,sans-serif;color:#0f172a;}
.sheet{width:210mm;min-height:297mm;background:#fff;margin:0 auto 14px;padding:16mm 16mm 12mm;box-shadow:0 6px 24px rgba(0,0,0,.25);}
.co{font-size:11px;font-weight:800;letter-spacing:2px;color:var(--kg);text-transform:uppercase;}
h1{font-size:22px;margin:4px 0 2px;} .sub{font-size:12px;color:var(--t2);border-bottom:2px solid var(--navy);padding-bottom:10px;margin-bottom:14px;}
.facts{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;margin-bottom:16px;}
.fact{display:flex;justify-content:space-between;font-size:12.5px;border-bottom:1px dotted var(--line);padding:4px 0;}
.fact .k{color:var(--t2);font-weight:600;} .fact .v{font-weight:600;text-transform:capitalize;}
.prog{height:12px;background:#eef1f6;border-radius:99px;overflow:hidden;margin:4px 0 16px;} .prog>span{display:block;height:100%;background:var(--kg);}
h2{font-size:14px;margin:14px 0 8px;}
table{width:100%;border-collapse:collapse;font-size:12px;} th{background:#f6f8fb;color:var(--t2);text-align:left;padding:7px 9px;font-size:10.5px;text-transform:uppercase;letter-spacing:.4px;border-bottom:1px solid var(--line);}
td{padding:7px 9px;border-bottom:1px solid var(--line);}
.notes{font-size:12px;color:#334155;line-height:1.6;white-space:pre-wrap;background:#f9fafb;border:1px solid var(--line);border-radius:8px;padding:10px 12px;margin-bottom:14px;}
.foot{font-size:9px;color:var(--t2);text-align:center;margin-top:16px;border-top:1px solid var(--line);padding-top:6px;}
@media print{body{background:#fff;}.sheet{box-shadow:none;margin:0;}@page{size:A4 portrait;margin:0;}}
</style></head><body><div class="sheet">
  <div class="co">KGreen Consulting &amp; Technologies Pvt Ltd</div>
  <h1>${esc(project.title || 'Project')}</h1>
  <div class="sub">Project Overview &middot; Printed: ${esc(todayFmt())}</div>
  <div class="facts">${facts.map(([k, v]) => `<div class="fact"><span class="k">${esc(k)}</span><span class="v">${esc(v == null || v === '' ? '—' : v)}</span></div>`).join('')}</div>
  <div style="font-size:12px;color:var(--t2);display:flex;justify-content:space-between"><span>Progress</span><span>${doneTasks}/${totalTasks} tasks · ${pct}%</span></div>
  <div class="prog"><span style="width:${pct}%"></span></div>
  ${project.notes ? `<h2>Notes</h2><div class="notes">${esc(project.notes)}</div>` : ''}
  <h2>Milestones</h2>
  <table><thead><tr><th>Milestone</th><th>Status</th><th>Start</th><th>End</th><th style="text-align:center">Tasks</th></tr></thead>
  <tbody>${msRows || '<tr><td colspan="5" style="text-align:center;color:#94a3b8">No milestones</td></tr>'}</tbody></table>
  <div class="foot">KGreen Consulting &amp; Technologies Pvt Ltd · Confidential · ${esc(project.project_number || '')}</div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},300);};<\/script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) { alert('Allow pop-ups to print.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
}

// Print the project TASK LIST (grouped by milestone) with planned & actual dates on A4 landscape.
export function printProjectTasks(project) {
  if (!project) return;
  const today = new Date().toISOString().slice(0, 10);
  const fmt = (s) => { if (!s) return '—'; const d = new Date(String(s).slice(0, 10) + 'T00:00:00Z'); return isNaN(d) ? s : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const ms = project.milestones || [];
  let body = '';
  for (const m of ms) {
    const tasks = m.tasks || [];
    body += `<tr class="msrow"><td colspan="7">◆ ${esc(m.title)} <span class="mm">(${fmt(m.start_date)} → ${fmt(m.end_date || m.due_date)})</span></td></tr>`;
    if (!tasks.length) { body += '<tr><td colspan="7" class="empty">No tasks</td></tr>'; continue; }
    for (const t of tasks) {
      const pct = t.pct_complete != null ? t.pct_complete : (t.done ? 100 : 0);
      const late = t.actual_end && t.planned_end && String(t.actual_end).slice(0, 10) > String(t.planned_end).slice(0, 10);
      const overdue = t.planned_end && String(t.planned_end).slice(0, 10) < today && !t.actual_end && pct < 100;
      const status = pct >= 100 ? 'Done' : overdue ? 'Overdue' : pct > 0 ? 'In progress' : 'Pending';
      body += `<tr>
        <td>${esc(t.title)}</td>
        <td>${esc(t.assignee_name || '—')}</td>
        <td>${fmt(t.planned_start)}</td><td>${fmt(t.planned_end)}</td>
        <td>${fmt(t.actual_start)}</td><td${late ? ' class="late"' : ''}>${fmt(t.actual_end)}</td>
        <td style="text-align:center">${pct}% <span class="st ${status.toLowerCase().replace(' ', '')}">${status}</span></td>
      </tr>`;
    }
  }
  const metaLine = [project.project_number, project.customer_name || project.company_name, project.manager_name && `Manager: ${project.manager_name}`].filter(Boolean).map(esc).join(' · ');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(project.title || 'Project')} — Tasks</title>
<style>
:root{--navy:#0F1E3D;--kg:#0B6623;--line:#e2e8f0;--t2:#64748b;--late:#dc2626;}
*{box-sizing:border-box} body{margin:0;background:#64748b;font-family:'Inter',system-ui,Arial,sans-serif;color:#0f172a;}
.sheet{width:297mm;min-height:210mm;background:#fff;margin:0 auto 14px;padding:12mm 12mm 10mm;box-shadow:0 6px 24px rgba(0,0,0,.25);}
.co{font-size:11px;font-weight:800;letter-spacing:2px;color:var(--kg);text-transform:uppercase;}
h1{font-size:19px;margin:3px 0 2px;} .sub{font-size:11px;color:var(--t2);border-bottom:2px solid var(--navy);padding-bottom:9px;margin-bottom:12px;}
table{width:100%;border-collapse:collapse;font-size:11.5px;} th{background:#f6f8fb;color:var(--t2);text-align:left;padding:7px 9px;font-size:10px;text-transform:uppercase;letter-spacing:.4px;border-bottom:1px solid var(--line);}
td{padding:6px 9px;border-bottom:1px solid var(--line);} .msrow td{background:#f9fafb;font-weight:800;padding-top:9px;} .msrow .mm{font-weight:500;color:var(--t2);font-size:10.5px;}
.empty{color:#94a3b8;} td.late{color:var(--late);font-weight:700;}
.st{font-size:9px;font-weight:800;padding:1px 6px;border-radius:99px;margin-left:4px;}
.st.done{background:#d1fae5;color:#065f46;} .st.inprogress{background:#fef3c7;color:#92400e;} .st.pending{background:#f1f5f9;color:#64748b;} .st.overdue{background:#fee2e2;color:#b91c1c;}
.foot{font-size:9px;color:var(--t2);text-align:center;margin-top:10px;border-top:1px solid var(--line);padding-top:6px;}
@media print{body{background:#fff;}.sheet{box-shadow:none;margin:0;}@page{size:A4 landscape;margin:10mm;} thead{display:table-header-group;} tr{break-inside:avoid;}}
</style></head><body><div class="sheet">
  <div class="co">KGreen Consulting &amp; Technologies Pvt Ltd</div>
  <h1>${esc(project.title || 'Project')} — Task Schedule</h1>
  <div class="sub">${metaLine} &middot; Printed: ${esc(todayFmt())}</div>
  <table><thead><tr><th>Task</th><th>Assignee</th><th>Planned Start</th><th>Planned End</th><th>Actual Start</th><th>Actual End</th><th style="text-align:center">Progress</th></tr></thead>
  <tbody>${body || '<tr><td colspan="7" class="empty" style="text-align:center">No tasks</td></tr>'}</tbody></table>
  <div class="foot">KGreen Consulting &amp; Technologies Pvt Ltd · Confidential · ${esc(project.project_number || '')}</div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},300);};<\/script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) { alert('Allow pop-ups to print.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
}
